import { Navigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { useStrategicParticipation } from "@/hooks/strategic/useStrategicParticipation";
import { Loader2 } from "lucide-react";

interface StrategicProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Componente que protege rotas do módulo estratégico.
 * 
 * Permite acesso se:
 * - Usuário é ADMIN ou SUPERVISOR
 * - Usuário PADRAO que participa de planos estratégicos (membro, responsável ou acesso aprovado)
 * 
 * Se não tem acesso, redireciona para /projects.
 */
export const StrategicProtectedRoute = ({
  children,
  redirectTo = '/projects'
}: StrategicProtectedRouteProps) => {
  const { getCurrentUserProfile, isLoadingPermissions } = usePermissions();
  const { hasStrategicParticipation, isLoading: isLoadingParticipation } = useStrategicParticipation();

  // Aguardar ambos os loadings
  if (isLoadingPermissions || isLoadingParticipation) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const profile = getCurrentUserProfile();
  const userRole = profile?.role || 'PADRAO';

  // ADMIN e SUPERVISOR sempre têm acesso
  if (['ADMIN', 'SUPERVISOR'].includes(userRole)) {
    return <>{children}</>;
  }

  // PADRAO com participação tem acesso
  if (hasStrategicParticipation) {
    return <>{children}</>;
  }

  // Sem acesso, redirecionar
  return <Navigate to={redirectTo} replace />;
};

export default StrategicProtectedRoute;
